
import { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { GroupRole } from "@/lib/types";
import { MembersListItem } from "./MembersListItem";
import { ConfirmationDialogs } from "./ConfirmationDialogs";
import { useMembersActions } from "@/hooks/groups/use-members-actions";
import { useMembersList } from "./useMembersList";

type MembersListProps = {
  groupId: string;
  currentUserRole: GroupRole;
  currentUserId: string;
};

export function MembersList({ 
  groupId, 
  currentUserRole, 
  currentUserId 
}: MembersListProps) {
  const {
    members,
    filteredMembers,
    loading,
    searchTerm,
    setSearchTerm,
    fetchMembers
  } = useMembersList(groupId);
  
  const { 
    memberIdToPromote, setMemberIdToPromote,
    memberIdToDemote, setMemberIdToDemote,
    memberIdToBan, setMemberIdToBan,
    memberIdToUnban, setMemberIdToUnban,
    memberIdToMute, setMemberIdToMute,
    memberIdToUnmute, setMemberIdToUnmute,
    memberIdToKick, setMemberIdToKick,
    confirmAction, setConfirmAction,
    handlePromoteMember, handleDemoteMember,
    handleBanMember, handleUnbanMember,
    handleMuteMember, handleUnmuteMember,
    handleKickMember
  } = useMembersActions(groupId, currentUserRole, currentUserId, members, fetchMembers);

  if (loading) {
    return <div className="text-center py-6">Loading members...</div>;
  }

  return (
    <div>
      <div className="flex items-center mb-4">
        <Input
          placeholder="Search members..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="space-y-2">
        {filteredMembers.length > 0 ? (
          filteredMembers.map(member => (
            <MembersListItem
              key={member.id}
              member={member}
              currentUserRole={currentUserRole}
              currentUserId={currentUserId}
              onPromote={() => {
                setMemberIdToPromote(member.id);
                setConfirmAction("promote");
              }}
              onDemote={() => {
                setMemberIdToDemote(member.id);
                setConfirmAction("demote");
              }}
              onBan={() => {
                setMemberIdToBan(member.id);
                setConfirmAction("ban");
              }}
              onUnban={() => {
                setMemberIdToUnban(member.id);
                setConfirmAction("unban");
              }}
              onMute={() => {
                setMemberIdToMute(member.id);
                setConfirmAction("mute");
              }}
              onUnmute={() => {
                setMemberIdToUnmute(member.id);
                setConfirmAction("unmute");
              }}
              onKick={() => {
                setMemberIdToKick(member.id);
                setConfirmAction("kick");
              }}
            />
          ))
        ) : (
          <p className="text-muted-foreground">No members found matching your search.</p>
        )}
      </div>

      <ConfirmationDialogs
        members={members}
        confirmAction={confirmAction}
        setConfirmAction={setConfirmAction}
        memberIdToPromote={memberIdToPromote}
        memberIdToDemote={memberIdToDemote}
        memberIdToBan={memberIdToBan}
        memberIdToUnban={memberIdToUnban}
        memberIdToMute={memberIdToMute}
        memberIdToUnmute={memberIdToUnmute}
        memberIdToKick={memberIdToKick}
        onPromote={handlePromoteMember}
        onDemote={handleDemoteMember}
        onBan={handleBanMember}
        onUnban={handleUnbanMember}
        onMute={handleMuteMember}
        onUnmute={handleUnmuteMember}
        onKick={handleKickMember}
      />
    </div>
  );
}
